import {
  FaReact,
  FaAngular,
  FaNodeJs,
  FaDocker,
  FaGitAlt,
  FaGithub,
  FaFigma,
  FaAws,
  FaJira,
  FaUsers,
  FaComments,
  FaRocket,
  FaHandsHelping,
  FaLanguage,
  FaLaptopCode,
  FaSitemap,
  FaBolt,
} from 'react-icons/fa';
import {
  SiTypescript,
  SiJavascript,
  SiTailwindcss,
  SiBootstrap,
  SiMongodb,
  SiPostgresql,
  SiMysql,
  SiSpringboot,
  SiExpress,
  SiTerraform,
  SiAnsible,
} from 'react-icons/si';

// ============================================================
// PORTFOLIO DATA — Dimitri Bonsou
// Développeur Fullstack · Ingénierie Applicative & Impact Métier
// ============================================================

export interface Project {
  id: number;
  name: string;
  tagline: string;
  problem: string;
  solution: string;
  approach: string;
  technologies: string[];
  image: string;
  liveUrl: string;
  category: string;
}

export interface Certification {
  id: number;
  title: string;
  issuer: string;
  date: string;
  summary: string;
  overview: string;
  skillsAcquired: string[];
  impact: string;
  image: string;
  verifyUrl: string;
}

export interface Skill {
  name: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

const portfolioData = {
  // --- Identity ---
  identity: {
    name: 'Dimitri Bonsou',
    initials: 'DB',
    title: 'Développeur Fullstack',
    tagline: 'Ingénierie Applicative · Rigueur Méthodologique · Impact Métier',
    description:
      'Développeur Fullstack, je conçois des solutions web modernes, performantes et évolutives. J’utilise l’IA générative comme un véritable levier pour accélérer le développement, résoudre des problèmes complexes et créer des produits fiables, intelligents et à forte valeur.',
    availability: 'Alternance dès Septembre 2026 · Stage de 6 mois dès Février 2027',
    location: 'France',
  },

  // --- Contact ---
  contact: {
    email: 'dimitribonsou26@gmail.com',
    github: 'https://github.com/Dimitribonsou',
    linkedin: 'https://linkedin.com/in/dimitribonsou',
  },

  // --- Navigation ---
  navigation: [
    { id: 'home', label: 'Accueil' },
    { id: 'about', label: 'À propos' },
    { id: 'stack', label: 'Stack' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'projects', label: 'Projets' },
    { id: 'contact', label: 'Contact' },
  ],

  // --- Stats ---
  stats: [
    { value: '10+', label: 'Projets livrés' },
    { value: '10+', label: 'Technologies' },
    { value: '40+', label: 'Repos GitHub' },
  ],

  // --- About: 3 Pillars ---
  pillars: [
    {
      title: 'Développement Fullstack & Mobile',
      description:
        'Conception d\'applications web et mobiles réactives (React, React Native, Angular, Node.js, Spring Boot) et conduite technique d\'équipes projets.',
      icon: FaLaptopCode,
    },
    {
      title: 'Conception SI & Architecture A-Z',
      description:
        'Diplômé Concepteur SI (Bac+3) et Mastère 2 Architecture Logicielle : modélisation de données (UML, Merise), design patterns et gouvernance des systèmes d\'information.',
      icon: FaSitemap,
    },
    {
      title: 'Intégration IA, Automation & Anglais',
      description:
        'Intégration d\'agents IA (Claude Code, Codex) dans les workflows, automation DevOps (Docker, Terraform, Ansible, CI/CD) et anglais technique en progression constante.',
      icon: FaBolt,
    },
  ],

  // --- About / Parcours Narrative & Core Values ---
  aboutNarrative: {
    title: 'Vision, Valeurs & Ce que j\'apporte à une équipe',
    paragraph:
      'Fort d\'un parcours certifié allant du DEC Web & Mobile (Bac+2), au Bachelor Concepteur de Systèmes d\'Information (Bac+3) jusqu\'au Mastère 2 Architecture & Développement Logiciel à 3iL Ingénieurs (Bac+5), j\'aborde chaque projet avec une rigueur d\'ingénierie globale. Mon approche associe modélisation préalable (UML), gestion Agile sur Jira, développement fullstack (React, React Native, Spring Boot) et accélération par des outils IA (Claude Code, Codex). Travaillant continuellement mon anglais technique pour interagir efficacement dans des environnements internationaux, mon objectif est d\'apporter une réelle valeur ajoutée au sein d\'une équipe exigeante.',
    values: [
      {
        title: 'Travail en Équipe & Leadership',
        desc: 'Expérience de Lead Dev Backend (gestion de 3 devs), écoute active et implication constante dans la réussite collective.',
        icon: FaUsers,
      },
      {
        title: 'Communication & Anglais Technique',
        desc: 'Transparence technique, rédaction claire et anglais professionnel en progression régulière pour collaborer sereinement.',
        icon: FaComments,
      },
      {
        title: 'Apprentissage Continu & Veille',
        desc: 'Veille technologique permanente, certifications régulières (freeCodeCamp, 3iL) et adoption rapide des nouveautés.',
        icon: FaRocket,
      },
      {
        title: 'Entraide & Partage de Connaissances',
        desc: 'Partage de bonnes pratiques, soutien technique aux collaborateurs et revues de code constructives.',
        icon: FaHandsHelping,
      },
    ],
  },

  // --- Tech Stack ---
  stack: {
    frontend: [
      { name: 'TypeScript', icon: SiTypescript },
      { name: 'React.js', icon: FaReact },
      { name: 'React Native', icon: FaReact },
      { name: 'Angular', icon: FaAngular },
      { name: 'JavaScript (ES6+)', icon: SiJavascript },
      { name: 'Tailwind CSS', icon: SiTailwindcss },
      { name: 'Bootstrap', icon: SiBootstrap },
    ],
    backend: [
      { name: 'Node.js', icon: FaNodeJs },
      { name: 'Spring Boot', icon: SiSpringboot },
      { name: 'Express', icon: SiExpress },
    ],
    database: [
      { name: 'MySQL', icon: SiMysql },
      { name: 'PostgreSQL', icon: SiPostgresql },
      { name: 'MongoDB', icon: SiMongodb },
    ],
    devops: [
      { name: 'Git', icon: FaGitAlt },
      { name: 'GitHub / GitLab CI', icon: FaGithub },
      { name: 'Docker', icon: FaDocker },
      { name: 'Terraform', icon: SiTerraform },
      { name: 'Ansible', icon: SiAnsible },
      { name: 'AWS', icon: FaAws },
      { name: 'Jira / Agile', icon: FaJira },
    ],
    design: [
      { name: 'Figma (UI/UX)', icon: FaFigma },
      { name: 'Prototypage & Design System', icon: FaFigma },
    ],
    languages: [
      { name: 'Français (Courant)', icon: FaLanguage },
      { name: 'Anglais Technique (Pratique régulière)', icon: FaLanguage },
    ],
  },

  // --- AI Workflow ---
  aiWorkflow: {
    title: 'Intégration de l\'IA & Ingénierie Logicielle',
    description:
      'J\'allie la rigueur de l\'architecture logicielle et de la gestion Agile (Jira) à la puissance des modèles et assistants IA (Claude Code, Codex). Cette synergie permet d\'automatiser le refactoring, d\'accélérer les tests unitaires et de concevoir des plateformes intelligentes ultra-performantes.',
    tools: ['Claude Code', 'Codex', 'Terraform', 'Ansible', 'GitLab CI / GitHub Actions'],
  },

  // --- Certifications & Diplômes ---
  certifications: [
    {
      id: 1,
      title: 'Mastère 2 Architecture & Développement Logiciel (Bac +5)',
      issuer: '3iL Ingénieurs, Limoges',
      date: '2025 - 2027 (En cours)',
      summary: 'Diplôme d\'ingénierie supérieure spécialisé en architecture logicielle et systèmes distribués.',
      overview: 'Cursus d\'ingénierie couvrant l\'analyse décisionnelle, la conception d\'architectures applicatives complexes, le Cloud Computing (AWS), l\'automatisation d\'infrastructures (Terraform, Ansible) et la gestion de projets informatiques d\'envergure sur Jira.',
      skillsAcquired: [
        'Architecture logicielle & Design Patterns avancés',
        'Modélisation UML & Systèmes distribués',
        'Développement Backend Java / Spring Boot & Node.js',
        'Cloud (AWS), Docker, Terraform & Ansible',
        'Management de projet informatique & Méthodes Agiles'
      ],
      impact: 'Confère une vision d\'ensemble du cycle de vie des applications distribuées, garantissant la conception de solutions hautement disponibles, scalables et sécurisées.',
      image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&h=400&fit=crop',
      verifyUrl: 'https://www.3il-ingenieurs.fr/nos-masteres/mastere-expert-en-architecture-et-developpement-logiciel-eadl/',
    },
    {
      id: 2,
      title: 'Bachelor Concepteur de Systèmes d\'Information (Bac +3)',
      issuer: '3iL Ingénieurs, Limoges',
      date: '2024 - 2025',
      summary: 'Diplôme certifiant la capacité à concevoir et analyser des systèmes d\'information d\'entreprise.',
      overview: 'Formation spécialisée dans l\'analyse des processus métiers, la modélisation de bases de données relationnelles et la conception d\'architectures logicielles applicatives n-tiers.',
      skillsAcquired: [
        'Conception & Modélisation SI (UML / Merise)',
        'Architecture n-tiers & APIs RESTful',
        'Ingénierie de bases de données (MySQL / PostgreSQL)',
        'Gestion de projet & Analyse des besoins'
      ],
      impact: 'Prouve la capacité à structurer un système d\'information de A à Z en apportant des solutions concrètes aux exigences métiers complexes.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop',
      verifyUrl: 'https://www.3il-ingenieurs.fr/nos-bachelors/bachelor-developpeur-web-full-stack/',
    },
    {
      id: 3,
      title: 'DEC Développement Web & Mobile (Bac +2)',
      issuer: 'CCNB - Collège communautaire du Nouveau-Brunswick',
      date: '2022 - 2024',
      summary: 'Diplôme d\'études collégiales axé sur la programmation web fullstack et mobile réactive.',
      overview: 'Cursus pratique intensif axé sur le développement d\'applications web et le développement mobile hybride avec React Native, la programmation client/serveur et l\'ergonomie UI/UX.',
      skillsAcquired: [
        'Développement Mobile React Native',
        'Développement Web Fullstack (React, HTML5, CSS3, JavaScript)',
        'Modélisation d\'applications & Schémas de données',
        'Intégration responsive & Prototypage Figma'
      ],
      impact: 'Pose les fondations solides du développement d\'applications web et mobiles réactives, permettant de réaliser des projets complets et fonctionnels.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop',
      verifyUrl: 'https://ccnb.ca/programmes/programmation-et-developpement-dapplications/',
    },
    {
      id: 4,
      title: 'Certification JavaScript Algorithms & Data Structures',
      issuer: 'freeCodeCamp',
      date: '2026',
      summary: 'Certification internationale validant la maîtrise de l\'algorithmique et des structures de données en JavaScript.',
      overview: 'Examen pratique certifiant la maîtrise approfondie de la syntaxe ES6+, de la programmation fonctionnelle, de la programmation orientée objet (POO), de la manipulation d\'algorithmes complexes et de structures de données.',
      skillsAcquired: [
        'Algorithmique avancée & Structures de données',
        'JavaScript ES6+ & Programmatic Logic',
        'Programmation Orientée Objet (POO)',
        'Programmation Fonctionnelle & Optimisation'
      ],
      impact: 'Atteste d\'une rigueur algorithmique élevée en JavaScript, garantie d\'un code performant et optimisé.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop',
      verifyUrl: 'https://www.freecodecamp.org/learn/javascript-v9/',
    },
  ] as Certification[],

  // --- Projects ---
  projects: [
    {
      id: 1,
      name: 'Commaxio',
      tagline: 'Plateforme SaaS de gestion commerciale & stocks',
      problem:
        'Gestion manuelle et dispersée des stocks et ventes pour les commerçants et PME, entraînant des erreurs de saisie, ruptures d\'approvisionnement et manque de visibilité analytique.',
      solution:
        'Plateforme SaaS de gestion de stock et commerciale centralisée, offrant un suivi des entrées/sorties en temps réel, la gestion multi-magasins et des tableaux de bord analytiques.',
      approach:
        'Conçu et développé de bout en bout (end-to-end) en tant que Product Builder : modélisation de la base de données relationnelle, développement de l\'API backend, création d\'une interface moderne et sécurisée, et mise en production sur commaxio.com.',
      technologies: ['React', 'TypeScript', 'Node.js', 'Express', 'MySQL', 'Tailwind CSS','PHP'],
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=500&fit=crop',
      liveUrl: 'https://commaxio.com',
      category: 'Product Builder',
    },
    {
      id: 2,
      name: 'TakeCare',
      tagline: 'Plateforme d\'entraide & gestion de bénévolat',
      problem:
        'Difficulté d\'organisation et de mise en relation sécurisée entre personnes vulnérables et bénévoles nécessitant un suivi fiable des demandes d\'aide.',
      solution:
        'Plateforme collaborative permettant d\'associer des bénévoles aux demandes d\'assistance avec gestion des rôles, sécurité des données et suivi temps réel.',
      approach:
        'Lead Développeur Backend & Chef d\'équipe (direction de 3 développeurs) : planification des sprints et répartition des tâches sur Jira, conception de l\'architecture Spring Boot, mise en place des pipelines CI/CD, conteneurisation Docker et rédaction des tests unitaires.',
      technologies: ['Spring Boot', 'Java', 'API REST', 'Docker', 'GitLab CI', 'Jira', 'PostgreSQL'],
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=500&fit=crop',
      liveUrl: 'https://gitlab.com/takecare_group/takecare',
      category: 'Backend / Lead Dev',
    },
    {
      id: 3,
      name: 'AlumniDocs',
      tagline: 'Plateforme de collecte étudiante',
      problem:
        'Processus manuel de collecte des informations de 2000+ étudiants — erreurs fréquentes, données dispersées, perte de temps.',
      solution:
        'Plateforme centralisée avec formulaires intelligents, validation temps réel et gestion sécurisée des données.',
      approach:
        'Architecture MVC, conception UML complète, API REST sécurisée (JWT), validation côté client et serveur, notifications email automatiques.',
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Node.js', 'Express', 'PostgreSQL'],
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=500&fit=crop',
      liveUrl: 'https://front-end-alumni-docs.vercel.app/',
      category: 'Fullstack',
    },
    {
      id: 4,
      name: '2SENS',
      tagline: 'Collaboration services publics',
      problem:
        'Communication inefficace entre services publics — délais, doublons, manque de traçabilité.',
      solution:
        'Espace collaboratif unifié avec messagerie temps réel, partage documentaire et gestion de tâches.',
      approach:
        'Architecture Angular modulaire, WebSocket pour le temps réel, design system complet, interface multi-rôles.',
      technologies: ['Angular', 'TypeScript', 'Bootstrap 5', 'Node.js', 'Socket.io', 'MongoDB'],
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop',
      liveUrl: 'https://2sens-webapp-test.vercel.app/home',
      category: 'Fullstack',
    },
    {
      id: 5,
      name: 'DépenseTrack',
      tagline: 'Gestion financière personnelle',
      problem:
        'Difficulté à suivre ses dépenses sans outil adapté — pas de vue d\'ensemble, budget mal maîtrisé.',
      solution:
        'Dashboard interactif avec catégorisation auto, graphiques temps réel et alertes budgétaires.',
      approach:
        'Stack MERN, API REST RESTful, authentification JWT, charts interactifs avec Recharts.',
      technologies: ['React', 'TypeScript', 'Node.js', 'Express', 'MySQL', 'Chart.js','PHP'],
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=500&fit=crop',
      liveUrl: 'https://depensetrack.commaxio.com',
      category: 'Fullstack',
    },
    {
      id: 6,
      name: 'Burger Express',
      tagline: 'Site web vitrine pour restaurant fast-food',
      problem:
        'Besoin d\'une présentation attractive en ligne de la carte des burgers, menus et options de réservation rapide.',
      solution:
        'Site web moderne et fluide présentant les spécialités du restaurant avec une interface responsive optimisée mobile.',
      approach:
        'Projet de mise en pratique d\'ingénierie web : intégration HTML5/CSS3 avec Tailwind CSS, programmation des interactions en JavaScript moderne.',
      technologies: ['HTML5', 'JavaScript', 'Tailwind CSS'],
      image: 'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?w=800&h=500&fit=crop',
      liveUrl: 'https://site-restaurant-burger.vercel.app/',
      category: 'Frontend',
    },
    {
      id: 7,
      name: 'Portfolio Développeur',
      tagline: 'Portfolio personnel & vitrine technique',
      problem:
        'Mettre en valeur son parcours, ses projets récents, ses certifications et son positionnement technique auprès des recruteurs.',
      solution:
        'Application web React performante avec design system sombre bleu, modals interactives et formulaire de contact direct.',
      approach:
        'Conception et développement complet : React, TypeScript, SCSS modulaire, intégration d\'outils IA et optimisation des performances web.',
      technologies: ['React', 'TypeScript', 'SCSS', 'GSAP'],
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&h=500&fit=crop',
      liveUrl: 'https://dimitribonsou-website.vercel.app/',
      category: 'Frontend / Design',
    },
    {
      id: 8,
      name: 'Site BISDC',
      tagline: 'Site institutionnel',
      problem:
        'Absence de présence web professionnelle pour une institution — communication limitée.',
      solution:
        'Site vitrine responsive avec design moderne, présentation des services et informations clés.',
      approach:
        'Maquettage Figma, intégration React avec composants réutilisables, optimisation SEO.',
      technologies: ['React', 'JavaScript', 'Bootstrap', 'CSS3'],
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=500&fit=crop',
      liveUrl: 'https://bisdc-awv40pow3gueka5q.builder-preview.com/',
      category: 'Frontend',
    },
  ] as Project[],
};

export default portfolioData;